<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <h2 class="text-sm text-gray-500">{{ vendors.length }} vendor(s)</h2>
      <div class="flex items-center gap-3">
        <label v-if="archivedCount > 0" class="flex items-center gap-2 text-sm text-gray-600">
          <input v-model="showArchived" type="checkbox" />
          Show archived ({{ archivedCount }})
        </label>
        <router-link :to="{ name: 'VendorCreate' }" class="btn-primary">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Vendor
        </router-link>
      </div>
    </div>

    <div v-if="error" class="card border-red-200 bg-red-50">
      <p class="text-sm font-semibold text-red-700 m-0">Failed to load vendors</p>
      <p class="text-xs text-red-700 mt-1 m-0">{{ error }}</p>
    </div>
    <div v-else-if="loading" class="card">
      <p class="text-sm text-gray-500 m-0">Loading vendors…</p>
    </div>
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div v-if="vendors.length === 0" class="card">
        <p class="text-sm text-gray-500 m-0">{{ archivedCount > 0 ? 'No active vendors.' : 'No vendors yet.' }}</p>
      </div>
      <router-link
        v-for="v in vendors"
        :key="v.id"
        :to="{ name: 'VendorDetail', params: { id: v.id } }"
        class="card hover:shadow-md transition-shadow block"
      >
        <div class="flex items-start gap-3">
          <svg class="w-5 h-5 text-blue-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 21h18M5 21V7l8-4v18M13 21V9l6 3v9M9 9h.01M9 12h.01M9 15h.01"
            />
          </svg>
          <div class="min-w-0">
            <div class="flex items-center gap-2 min-w-0">
              <div class="font-semibold text-gray-900 truncate">{{ vendorDisplayName(v) }}</div>
              <span v-if="isArchived(v)" class="badge bg-gray-100 text-gray-500 shrink-0">Archived</span>
            </div>
            <div class="text-xs text-gray-400 mt-0.5 truncate">{{ v.email ?? 'No email' }}</div>
            <div v-if="v.city || v.country" class="text-xs text-gray-400 mt-0.5">
              {{ [v.city, v.country].filter(Boolean).join(', ') }}
            </div>
          </div>
        </div>
      </router-link>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { useVendorStore } from '@/store/vendorStore';
  import { isArchived, vendorDisplayName } from '@/utils/vendorArchive';

  const vendorStore = useVendorStore();
  const showArchived = ref(false);
  const vendors = computed(() => (showArchived.value ? vendorStore.vendors : vendorStore.activeVendors));
  const archivedCount = computed(() => vendorStore.vendors.length - vendorStore.activeVendors.length);
  const loading = computed(() => vendorStore.loading);
  const error = computed(() => vendorStore.error);

  onMounted(() => {
    void vendorStore.fetchVendors();
  });
</script>
